/* eslint-disable @typescript-eslint/no-explicit-any */

import type { Variants } from "motion/react"
import type { MutableRefObject } from "react"
import {
	forwardRef,
	memo,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react"
import { isNodeSelection, isTextSelection } from "@tiptap/core"
import Placeholder from "@tiptap/extension-placeholder"
import Table from "@tiptap/extension-table"
import TableCell from "@tiptap/extension-table-cell"
import TableHeader from "@tiptap/extension-table-header"
import TableRow from "@tiptap/extension-table-row"
import TextAlign from "@tiptap/extension-text-align"
import Youtube from "@tiptap/extension-youtube"
import {
	BubbleMenu,
	EditorContent,
	findParentNode,
	useEditor,
} from "@tiptap/react"
import { AnimatePresence, motion } from "motion/react"
import {
	IconTextBoldOutline18 as BoldIcon,
	IconCode2Outline18 as Code2Icon,
	IconTextItalicOutline18 as ItalicIcon,
	IconLinkOutline18 as LinkIcon,
	IconTextStrikethroughOutline18 as StrikethroughIcon,
	IconTableOutline18 as TableIcon,
	IconLinkSlashOutline18 as UnlinkIcon,
} from "nucleo-ui-outline-18"

import type { GalleryImage } from "@productlane/lib/src/tiptap/extensions/image-gallery-extension"
import {
	cn,
	FileEmbedExtension,
	getClipboardFiles,
	getDefaultExtensions,
	Loom,
	PdfExtension,
	VideoExtension,
} from "@productlane/lib/src/browser"
import {
	isPreviewableDocument,
	MEDIA_UPLOAD_ACCEPT,
} from "@productlane/lib/src/document-preview/kinds"
import { IframeExtension } from "@productlane/lib/src/tiptap/extensions/iframe-extension"
import { MintlifySlotPlaceholders } from "@productlane/lib/src/tiptap/extensions/mintlify/slot-placeholders"
import { TabExtension } from "@productlane/lib/src/tiptap/extensions/tabs-extension"
import {
	CustomTextAlign,
	InlineCode,
	Tooltip,
	typographyVariants,
} from "@productlane/ui"
import CustomCodeBlock, {
	useAllCodeBlockLanguages,
} from "@productlane/ui/src/components/editor/extenstions/custom-code-block"
import CustomInlineCode from "@productlane/ui/src/components/editor/extenstions/custom-inline-code"
import CustomLink from "@productlane/ui/src/components/editor/extenstions/custom-link"
import { BlockquoteIcon } from "@productlane/ui/src/icons/project/blockquote-icon"

import type { EditorView } from "@tiptap/pm/view"
import type { Editor, EditorEvents, Extensions } from "@tiptap/react"
import { ALLOWED_FILE_SIZE_IN_MB } from "@/constants"
import { api } from "@/lib/api"
import { processCoverImage } from "@/lib/process-cover-image"
import { toast } from "../toast"
import { CalloutVariantMenu } from "./callout-variant-menu"
import { DEFAULT_EDITOR_PROPS } from "./editor-config"
import { CustomImageExtension } from "./extensions/custom-image-extension"
import { EmojiSuggestion } from "./extensions/emoji"
import { applyGalleryImageAlt, applyImageAlt } from "./extensions/image-alt"
import { ImageGalleryWithNodeView } from "./extensions/image-gallery-node-view"
import { withMintlifyNodeViews } from "./extensions/mintlify/with-node-views"
import { CodeGroupWithNodeView } from "./extensions/panels/code-group-node-view"
import { TabsWithNodeView } from "./extensions/panels/tabs-node-view"
import { uploadMediaFile } from "./extensions/slash-commands/commands/media-command"
import slashCommands from "./extensions/slash-commands/slash-commands"
import { suggestion } from "./extensions/slash-commands/suggestion"
import { TableWithControls } from "./extensions/table-controls"
import { TrailingNode } from "./extensions/trailing-node"
import { HelpArticleTriggerExtension } from "./help-articles/help-articles"
import { HelpArticlesBridge } from "./help-articles/help-articles-bridge"
import { liveSelectionRect } from "./live-selection-rect"
import { insertMarkdownPaste } from "./markdown-paste"
import { EditorMenuHeadingDropdown } from "./menu/dropdowns/editor-menu-heading-dropdown"
import { EditorMenuListDropdown } from "./menu/dropdowns/editor-menu-lists-dropdown"
import { EditorMenuEmbeededMediaDropdown } from "./menu/dropdowns/editor-menu-media-dropdown"
import { EditorMenuTableDropdown } from "./menu/dropdowns/editor-menu-table-dropdown"
import { EditorMenuButton } from "./menu/editor-menu-button"
import { EditorMenuInput } from "./menu/editor-menu-input"
import { shouldShowBubbleMenu } from "./should-show-bubble-menu"
import { uploadedImageAttrs } from "./uploaded-image-attrs"

interface IProps {
	editable?: boolean
	onClick?: (
		this: unknown,
		view: any,
		pos: number,
		event: MouseEvent,
	) => boolean
	initialContent?: any
	onUpdateRef?: MutableRefObject<(props: EditorEvents["update"]) => void>
	onUpdate?: (props: EditorEvents["update"]) => void
	autofocus?: boolean | null | "end" | "start"
	onFocus?: (params: EditorEvents["focus"]) => void
	placeholder?: string
	className?: string
	style?: any
	extensions?: Extensions
	trailingNode?: boolean
	spellCheck?: boolean
	collaborative?: boolean
	onCreate?: (props: EditorEvents["create"]) => void
}

const bubbleMenuVariants: Variants = {
	open: {
		opacity: 1,
		scale: 1,
		width: "fit-content",
	},
	addingLink: {
		opacity: 1,
		scale: 1,
		width: "fit-content",
	},
	closed: {
		opacity: 0,
		scale: 0.95,
	},
}

export const DefaultEditor = memo(
	forwardRef<any, IProps>(
		(
			{
				editable = true,
				onClick,
				initialContent,
				onUpdateRef,
				onUpdate,
				autofocus,
				onFocus,
				placeholder,
				className,
				style,
				extensions = [],
				trailingNode = true,
				spellCheck,
				collaborative = false,
				onCreate,
			},
			ref,
		) => {
			const containerRef = useRef<HTMLDivElement>(null)
			const userSelectingRef = useRef(false)

			const [link, setLink] = useState("")
			const [youtubeUrl, setYoutubeUrl] = useState("")
			const [loomUrl, setLoomUrl] = useState("")
			const [isAddingLink, setIsAddingLink] = useState<boolean>(false)
			const [isAddingMedia, setIsAddingMedia] = useState<boolean>(false)
			const [isAddingYoutube, setIsAddingYoutube] = useState<boolean>(false)
			const [isAddingLoom, setIsAddingLoom] = useState<boolean>(false)
			const getSignedUrlMutation = api.file.getUrl.useMutation()
			const fileInputRef = useRef<HTMLInputElement>(null)
			const galleryInputRef = useRef<HTMLInputElement>(null)
			const helpArticlesAnchorRef = useRef<HTMLDivElement>(null)

			const toggleAddingState = (
				type: "link" | "media" | "youtube" | "loom" | "image",
			) => {
				setIsAddingLink(type === "link" ? !isAddingLink : false)
				setIsAddingMedia(type === "media" ? !isAddingMedia : false)
				setIsAddingYoutube(type === "youtube" ? !isAddingYoutube : false)
				setIsAddingLoom(type === "loom" ? !isAddingLoom : false)
			}

			const describeImageMutation = api.docsImage.describe.useMutation()

			// The agent reads the stored description; the alt written back here is
			// what a screen reader and the search index see on the page itself.
			const describeImage = useCallback(
				async (fileUrl: string, view: EditorView) => {
					try {
						const { altText } = await describeImageMutation.mutateAsync({
							url: fileUrl,
						})
						if (!altText) return

						const tr = applyImageAlt(view.state, fileUrl, altText)
						if (tr) view.dispatch(tr)
					} catch {
						// The row is written server-side either way, so a failure here
						// costs the alt attribute and nothing else.
					}
				},
				[describeImageMutation],
			)

			const describeGalleryImage = useCallback(
				async (fileUrl: string, view: EditorView) => {
					try {
						const { altText } = await describeImageMutation.mutateAsync({
							url: fileUrl,
						})
						if (!altText) return

						const tr = applyGalleryImageAlt(view.state, fileUrl, altText)
						if (tr) view.dispatch(tr)
					} catch {
						// The row is written server-side either way, so a failure here
						// costs the alt attribute and nothing else.
					}
				},
				[describeImageMutation],
			)

			const uploadMedia = useCallback(
				async (file: File, view: EditorView) => {
					const isImage = file.type.startsWith("image")
					const isVideo = file.type.startsWith("video")
					const isDocument = isPreviewableDocument({
						fileName: file.name,
						mimeType: file.type,
					})

					try {
						let fileToUpload = file
						let originalUrl: string | undefined

						if (isImage) {
							fileToUpload = await processCoverImage(file)

							const originalFileSize = (file.size / 1024 / 1024).toFixed(4)
							if (Number(originalFileSize) < ALLOWED_FILE_SIZE_IN_MB) {
								const {
									uploadUrl: originalUploadUrl,
									fileUrl: originalFileUrl,
								} = await getSignedUrlMutation.mutateAsync({
									fileName: `original_${file.name}`,
									fileType: file.type,
								})

								const originalHeaders = { "Content-Type": file.type }
								const originalRes = await fetch(originalUploadUrl, {
									method: "PUT",
									body: file,
									headers: originalHeaders,
								})
								if (originalRes.ok) {
									originalUrl = originalFileUrl
								}
							}
						}

						const { uploadUrl, fileUrl } =
							await getSignedUrlMutation.mutateAsync({
								fileName: fileToUpload.name,
								fileType: fileToUpload.type,
							})

						const filesize = (fileToUpload.size / 1024 / 1024).toFixed(4)
						if (Number(filesize) >= ALLOWED_FILE_SIZE_IN_MB) {
							throw new Error(
								`Media files need to be less than ${ALLOWED_FILE_SIZE_IN_MB}mb in size.`,
							)
						}

						const headers = {
							"Content-Type": fileToUpload.type,
						}
						const res = await fetch(uploadUrl, {
							method: "PUT",
							body: fileToUpload,
							headers,
						})

						if (!res.ok) {
							throw new Error("File upload failed")
						}

						if (isImage) {
							const attrs = await uploadedImageAttrs({
								original: file,
								processed: fileToUpload,
								originalUrl,
								processedUrl: fileUrl,
							})
							const node = view.state.schema.nodes.image?.create(attrs)
							if (!node) {
								throw new Error("Failed to create image node")
							}
							const transaction = view.state.tr.replaceSelectionWith(node)
							view.dispatch(transaction)
							toast.success({ title: "Image uploaded" })
							void describeImage(attrs.src, view)
							return
						}

						if (isVideo) {
							const node = view.state.schema.nodes.video?.create({
								src: fileUrl,
								controls: true,
							})
							if (!node) {
								throw new Error("Failed to create video node")
							}
							const transaction = view.state.tr.replaceSelectionWith(node)
							view.dispatch(transaction)
							toast.success({ title: "Video uploaded" })
							return
						}

						if (isDocument) {
							const node = view.state.schema.nodes.fileEmbed?.create({
								src: fileUrl,
								fileName: file.name,
								mimeType: file.type || null,
								fileSize: file.size,
							})
							if (!node) {
								throw new Error("Failed to create document node")
							}
							const transaction = view.state.tr.replaceSelectionWith(node)
							view.dispatch(transaction)
							toast.success({ title: `${file.name} uploaded` })
							return
						}
					} catch (error) {
						toast.error({
							title:
								error instanceof Error
									? error.message
									: "Failed to upload media",
						})
					}
				},
				[getSignedUrlMutation, describeImage],
			)

			const editor = useEditor({
				autofocus,
				// eslint-disable-next-line @typescript-eslint/no-empty-function
				onFocus: onFocus ? onFocus : () => {},
				editorProps: {
					...DEFAULT_EDITOR_PROPS,
					attributes: {
						class: cn(
							DEFAULT_EDITOR_PROPS.attributes.class,
							typographyVariants({
								variant: "editor",
								mode: "invert",
							}),
						),
						...(spellCheck === false && { spellcheck: "false" }),
					},
					handleClick: onClick,
					handleDOMEvents: {
						mousedown: () => {
							userSelectingRef.current = true
							return false
						},
						keydown: () => {
							userSelectingRef.current = true
							return false
						},
						blur: () => {
							userSelectingRef.current = false
							return false
						},
					},
					handleDrop: (view, event, _slice, moved) => {
						if (
							!moved &&
							event.dataTransfer &&
							event.dataTransfer.files &&
							event.dataTransfer.files[0]
						) {
							const item = event.dataTransfer.files[0] // The dropped file
							if (
								item.type.startsWith("image") ||
								item.type.startsWith("video") ||
								isPreviewableDocument({
									fileName: item.name,
									mimeType: item.type,
								})
							) {
								// eslint-disable-next-line @typescript-eslint/no-floating-promises
								uploadMedia(item, view)
								return true
							}
						}
						return false
					},
					handlePaste: (view, event) => {
						const markdownExtensions = [
							...withMintlifyNodeViews(
								getDefaultExtensions({
									image: false,
									inlineImage: true,
									imageGallery: false,
									bulletListAccentColor: true,
									link: false,
									panels: false,
								}),
							),

							CustomImageExtension,
							ImageGalleryWithNodeView,
							TabsWithNodeView,
							TabExtension,
							CodeGroupWithNodeView,
							TextAlign.configure({
								types: ["heading", "paragraph"],
							}),
							CustomInlineCode,
							CustomCodeBlock,
							CustomLink.configure({
								borderColor: "var(--portal-accent-color)",
							}),
							CustomTextAlign,
							Table.configure({
								resizable: true,
							}),
							TableCell,
							TableRow,
							TableHeader,
							...extensions,
						]
						if (
							insertMarkdownPaste({
								view,
								event,
								editor,
								extensions: markdownExtensions,
							})
						) {
							return true
						}

						// Existing media file handling
						const media = getClipboardFiles(event.clipboardData?.items).find(
							(file) =>
								file.type.startsWith("image") ||
								file.type.startsWith("video") ||
								isPreviewableDocument({
									fileName: file.name,
									mimeType: file.type,
								}),
						)
						if (media) {
							// eslint-disable-next-line @typescript-eslint/no-floating-promises
							uploadMedia(media, view)
							return true
						}
						return false
					},
					// dont convert <img> to actual images when html is pasted
					transformPastedHTML(html) {
						return html.replace(/<img.*?>/g, "")
					},
				},

				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				content: collaborative ? undefined : initialContent,
				onCreate: (props) => onCreate?.(props),
				onUpdate: (props) => {
					onUpdateRef?.current?.(props)
					onUpdate?.(props)
				},
				editable,
				extensions: [
					...withMintlifyNodeViews(
						getDefaultExtensions({
							image: false,
							inlineImage: true,
							imageGallery: false,
							bulletListAccentColor: true,
							link: false,
							panels: false,
							history: !collaborative,
						}),
					),

					EmojiSuggestion,
					CustomImageExtension,
					ImageGalleryWithNodeView,
					TabsWithNodeView,
					TabExtension,
					CodeGroupWithNodeView,
					TextAlign.configure({
						types: ["heading", "paragraph"],
					}),
					Placeholder.configure({
						showOnlyWhenEditable: false,
						placeholder,
					}),
					MintlifySlotPlaceholders,
					Youtube.configure({
						controls: true,
						autoplay: false,
						// @ts-expect-error nice ts
						width: "100%",
						// @ts-expect-error nice ts
						height: "auto",
						HTMLAttributes: {
							class: "aspect-video",
						},
						nocookie: true,
					}),
					Loom.configure({
						allowFullscreen: true,
						width: "100%",
						height: "auto",
						HTMLAttributes: {
							class: "aspect-video",
						},
					}),
					IframeExtension.configure({
						allowFullscreen: true,
					}),
					VideoExtension,
					PdfExtension,
					FileEmbedExtension,
					TableWithControls.configure({
						resizable: true,
					}),
					TableCell,
					TableRow,
					TableHeader,
					slashCommands.configure({
						suggestion,
					}),
					...(trailingNode ? [TrailingNode] : []),
					CustomInlineCode,
					CustomCodeBlock,
					CustomLink.configure({
						borderColor: "var(--portal-accent-color)",
					}),
					CustomTextAlign,
					HelpArticleTriggerExtension,
					...extensions,
				],
			})

			const getIsInTable = (editor: Editor) => {
				if (!editor?.state.selection) {
					return false
				}
				const node = findParentNode((node) => {
					return ["tableCell", "tableHeader", "tableRow"].includes(
						node.type.name,
					)
				})(editor?.state.selection)
				return !!node
			}

			const isInTable = useMemo(() => {
				if (!editor) {
					return false
				}
				return getIsInTable(editor)
				// eslint-disable-next-line react-hooks/exhaustive-deps
			}, [editor?.state.selection])

			useAllCodeBlockLanguages(editor)

			useImperativeHandle(ref, () => ({
				getEditorInstance() {
					return editor
				},
			}))

			const mediaInsertPosRef = useRef<number | null>(null)
			const galleryTargetRef = useRef<{
				insertPos?: number
				onImages?: (images: GalleryImage[]) => void
			} | null>(null)

			// Listen for media upload requests from slash command
			useEffect(() => {
				const handleMediaUploadRequest = (e: Event) => {
					const detail = (e as CustomEvent<{ insertPos?: number }>).detail
					mediaInsertPosRef.current =
						typeof detail?.insertPos === "number" ? detail.insertPos : null
					fileInputRef.current?.click()
				}

				window.addEventListener(
					"media-upload-request",
					handleMediaUploadRequest,
				)

				return () => {
					window.removeEventListener(
						"media-upload-request",
						handleMediaUploadRequest,
					)
				}
			}, [])

			useEffect(() => {
				const handleGalleryUploadRequest = (e: Event) => {
					galleryTargetRef.current =
						(
							e as CustomEvent<{
								insertPos?: number
								onImages?: (images: GalleryImage[]) => void
							}>
						).detail ?? null
					galleryInputRef.current?.click()
				}

				window.addEventListener(
					"gallery-upload-request",
					handleGalleryUploadRequest,
				)

				return () => {
					window.removeEventListener(
						"gallery-upload-request",
						handleGalleryUploadRequest,
					)
				}
			}, [])

			const handleGalleryInputChange = useCallback(
				async (e: React.ChangeEvent<HTMLInputElement>) => {
					const files = [...(e.target.files ?? [])].filter((file) =>
						file.type.startsWith("image"),
					)
					const target = galleryTargetRef.current
					galleryTargetRef.current = null
					if (galleryInputRef.current) galleryInputRef.current.value = ""
					if (files.length === 0 || !editor) return

					try {
						const uploads = await Promise.all(
							files.map((file) =>
								uploadMediaFile(file, getSignedUrlMutation, {
									processImage: false,
								}),
							),
						)
						const images: GalleryImage[] = uploads.map((upload) => ({
							src: upload.url,
							alt: null,
						}))

						if (target?.onImages) {
							target.onImages(images)
						} else {
							const chain = editor.chain()
							const positioned =
								typeof target?.insertPos === "number"
									? chain.focus().setTextSelection(target.insertPos)
									: chain.focus()
							positioned.setImageGallery({ images }).run()
						}

						toast.success({
							title:
								images.length === 1
									? "1 image added"
									: `${images.length} images added`,
						})
						images.forEach(
							(image) => void describeGalleryImage(image.src, editor.view),
						)
					} catch (error) {
						toast.error({
							title:
								error instanceof Error
									? error.message
									: "Failed to upload images",
						})
					}
				},
				[editor, getSignedUrlMutation, describeGalleryImage],
			)

			const handleFileInputChange = useCallback(
				async (e: React.ChangeEvent<HTMLInputElement>) => {
					const file = e.target.files?.[0]
					if (!file || !editor) return

					const isImage = file.type.startsWith("image")
					const isVideo = file.type.startsWith("video")
					const isDocument = isPreviewableDocument({
						fileName: file.name,
						mimeType: file.type,
					})

					try {
						let fileToUpload = file
						let originalUrl: string | undefined

						if (isImage) {
							fileToUpload = await processCoverImage(file)

							const originalFileSize = (file.size / 1024 / 1024).toFixed(4)
							if (Number(originalFileSize) < ALLOWED_FILE_SIZE_IN_MB) {
								const {
									uploadUrl: originalUploadUrl,
									fileUrl: originalFileUrl,
								} = await getSignedUrlMutation.mutateAsync({
									fileName: `original_${file.name}`,
									fileType: file.type,
								})

								const originalHeaders = { "Content-Type": file.type }
								const originalRes = await fetch(originalUploadUrl, {
									method: "PUT",
									body: file,
									headers: originalHeaders,
								})
								if (originalRes.ok) {
									originalUrl = originalFileUrl
								}
							}
						}

						const { uploadUrl, fileUrl } =
							await getSignedUrlMutation.mutateAsync({
								fileName: fileToUpload.name,
								fileType: fileToUpload.type,
							})

						const filesize = (fileToUpload.size / 1024 / 1024).toFixed(4)
						if (Number(filesize) >= ALLOWED_FILE_SIZE_IN_MB) {
							throw new Error(
								`Media files need to be less than ${ALLOWED_FILE_SIZE_IN_MB}mb in size.`,
							)
						}

						const headers = {
							"Content-Type": fileToUpload.type,
						}
						const res = await fetch(uploadUrl, {
							method: "PUT",
							body: fileToUpload,
							headers,
						})

						if (!res.ok) {
							throw new Error("File upload failed")
						}

						const insertPos = mediaInsertPosRef.current
						const restoreSelection = (
							chain: ReturnType<typeof editor.chain>,
						) =>
							insertPos != null
								? chain.focus().setTextSelection(insertPos)
								: chain.focus()

						if (isImage) {
							const attrs = await uploadedImageAttrs({
								original: file,
								processed: fileToUpload,
								originalUrl,
								processedUrl: fileUrl,
							})
							restoreSelection(editor.chain()).setImage(attrs).run()
							toast.success({ title: "Image uploaded" })
							void describeImage(attrs.src, editor.view)
						} else if (isVideo) {
							restoreSelection(editor.chain()).setVideo({ src: fileUrl }).run()
							toast.success({ title: "Video uploaded" })
						} else if (isDocument) {
							restoreSelection(editor.chain())
								.setFileEmbed({
									src: fileUrl,
									fileName: file.name,
									mimeType: file.type || null,
									fileSize: file.size,
								})
								.run()
							toast.success({ title: `${file.name} uploaded` })
						}

						mediaInsertPosRef.current = null
					} catch (error) {
						toast.error({
							title:
								error instanceof Error
									? error.message
									: "Failed to upload media",
						})
					}

					// Reset input
					if (fileInputRef.current) {
						fileInputRef.current.value = ""
					}
				},
				[editor, getSignedUrlMutation, describeImage],
			)

			const handleInputActions = {
				link: {
					value: link,
					onChange: setLink,
					placeholder: "https://productlane.com",
					onEnter: () => {
						editor
							?.chain()
							.focus()
							.extendMarkRange("link")
							.setLink({ href: link, target: "_blank" })
							.run()
						setLink("")
						setIsAddingLink(false)
					},
					onEscape: () => {
						setLink("")
						setIsAddingLink(false)
					},
				},
				youtube: {
					value: youtubeUrl,
					onChange: setYoutubeUrl,
					placeholder: "Youtube URL",
					onEnter: () => {
						editor?.commands.setYoutubeVideo({ src: youtubeUrl })
						setYoutubeUrl("")
						setIsAddingYoutube(false)
					},
					onEscape: () => {
						setYoutubeUrl("")
						setIsAddingYoutube(false)
					},
				},
				loom: {
					value: loomUrl,
					onChange: setLoomUrl,
					placeholder: "Loom Video URL",
					onEnter: () => {
						editor?.commands.setLoomVideo({ src: loomUrl })
						setLoomUrl("")
						setIsAddingLoom(false)
					},
					onEscape: () => {
						setLoomUrl("")
						setIsAddingLoom(false)
					},
				},
			}

			if (!editor) {
				return
			}

			return (
				<AnimatePresence>
					{editable && (
						<BubbleMenu
							key="menu"
							tippyOptions={{
								getReferenceClientRect: liveSelectionRect(editor),
								duration: 100,
								placement: "top",
								zIndex: 40,
								popperOptions: {
									modifiers: [
										{
											name: "customStyle",
											enabled: true,
											phase: "beforeWrite",
											fn({ state }) {
												state.elements.popper.classList.add("transition-all")
												const popper = state.elements.popper
												const tippyBox = popper.firstChild as HTMLElement | null
												if (tippyBox) {
													tippyBox.style.maxWidth = "none"
												}
											},
										},
									],
								},
							}}
							shouldShow={({ editor, view, state, from, to }) => {
								const menu = containerRef.current
								return shouldShowBubbleMenu({
									isEditable: editor.isEditable,
									hasEditorFocus: view.hasFocus(),
									isUserSelection: userSelectingRef.current,
									isMenuFocused: !!menu?.contains(document.activeElement),
									isSelectionEmpty: !state.selection || state.selection.empty,
									isEmptyTextBlock:
										!state.doc.textBetween(from, to).length &&
										isTextSelection(state.selection),
									isInTable: getIsInTable(editor),
									isDividerSelection:
										isNodeSelection(state.selection) &&
										state.selection.node.type.name === "horizontalRule",
								})
							}}
							editor={editor}
						>
							<motion.div
								ref={containerRef}
								id="bubble-menu-container"
								variants={bubbleMenuVariants}
								animate={
									isAddingLink || isAddingYoutube || isAddingLoom
										? "addingLink"
										: "open"
								}
								exit="closed"
								className="flex origin-bottom flex-col whitespace-nowrap rounded-lg border border-gray-200 bg-white p-1 font-medium text-gray-500 shadow-sm ring-1 ring-gray-300/30 dark:border-gray-800 dark:bg-gray-850 dark:text-gray-200 dark:ring-gray-700/40"
							>
								{isAddingLink && (
									<EditorMenuInput {...handleInputActions.link} />
								)}
								{isAddingYoutube && (
									<EditorMenuInput {...handleInputActions.youtube} />
								)}

								{isAddingLoom && (
									<EditorMenuInput {...handleInputActions.loom} />
								)}

								<div className="flex gap-1">
									<EditorMenuHeadingDropdown
										editor={editor}
										containerRef={containerRef}
									/>

									<Tooltip shouldWrapChildren label="Bold">
										<EditorMenuButton
											onClick={() =>
												editor
													.chain()
													.focus(undefined, { scrollIntoView: false })
													.toggleBold()
													.run()
											}
											isActive={editor.isActive("bold")}
										>
											<BoldIcon className="h-4 w-4" />
										</EditorMenuButton>
									</Tooltip>

									<Tooltip shouldWrapChildren label="Italic">
										<EditorMenuButton
											onClick={() =>
												editor
													.chain()
													.focus(undefined, { scrollIntoView: false })
													.toggleItalic()
													.run()
											}
											isActive={editor.isActive("italic")}
										>
											<ItalicIcon className="h-4 w-4" />
										</EditorMenuButton>
									</Tooltip>

									<Tooltip shouldWrapChildren label="Strikethrough">
										<EditorMenuButton
											onClick={() =>
												editor
													.chain()
													.focus(undefined, { scrollIntoView: false })
													.toggleStrike()
													.run()
											}
											isActive={editor.isActive("strike")}
										>
											<StrikethroughIcon className="h-4 w-4" />
										</EditorMenuButton>
									</Tooltip>

									<Tooltip shouldWrapChildren label="Link">
										<EditorMenuButton
											onClick={(event) => {
												event.preventDefault()
												toggleAddingState("link")
											}}
											isActive={isAddingLink}
										>
											<LinkIcon className="h-4 w-4" />
										</EditorMenuButton>
									</Tooltip>

									{editor.isActive("link") && (
										<Tooltip shouldWrapChildren label="Remove link">
											<EditorMenuButton
												onClick={(event) => {
													event.preventDefault()
													editor
														.chain()
														.focus(undefined, { scrollIntoView: false })
														.unsetLink()
														.run()
												}}
												isActive={editor.isActive("link")}
											>
												<UnlinkIcon className="h-4 w-4" />
											</EditorMenuButton>
										</Tooltip>
									)}

									<Tooltip shouldWrapChildren label="Blockquote">
										<EditorMenuButton
											onClick={() =>
												editor
													.chain()
													.focus(undefined, { scrollIntoView: false })
													.toggleBlockquote()
													.run()
											}
											isActive={editor.isActive("blockquote")}
										>
											<BlockquoteIcon className="h-4 w-4" />
										</EditorMenuButton>
									</Tooltip>

									<CalloutVariantMenu editor={editor} />

									<Tooltip shouldWrapChildren label="Inline code">
										<EditorMenuButton
											onClick={() =>
												editor
													.chain()
													.focus(undefined, { scrollIntoView: false })
													.toggleCode()
													.run()
											}
											isActive={editor.isActive("code")}
										>
											<InlineCode className="h-4 w-4" />
										</EditorMenuButton>
									</Tooltip>

									<Tooltip shouldWrapChildren label="Code block">
										<EditorMenuButton
											onClick={() =>
												editor
													.chain()
													.focus(undefined, { scrollIntoView: false })
													.toggleCodeBlock()
													.run()
											}
											isActive={editor.isActive("codeBlock")}
										>
											<Code2Icon className="h-4 w-4" />
										</EditorMenuButton>
									</Tooltip>

									{isInTable ? (
										<EditorMenuTableDropdown
											editor={editor}
											containerRef={containerRef}
										></EditorMenuTableDropdown>
									) : (
										<Tooltip shouldWrapChildren label="Table">
											<EditorMenuButton
												onClick={() =>
													editor
														.chain()
														.focus()
														.insertTable({
															rows: 3,
															cols: 3,
															withHeaderRow: true,
														})
														.run()
												}
												isActive={editor.isActive("table")}
											>
												<TableIcon className="h-4 w-4" />
											</EditorMenuButton>
										</Tooltip>
									)}

									<EditorMenuEmbeededMediaDropdown
										onTriggerMediaUpload={() => fileInputRef.current?.click()}
										toggleAddingState={toggleAddingState}
										activeState={{
											isAddingMedia,
											isAddingYoutube,
											isAddingLoom,
										}}
										containerRef={containerRef}
									/>

									<EditorMenuListDropdown
										editor={editor}
										containerRef={containerRef}
									/>
								</div>
							</motion.div>
						</BubbleMenu>
					)}
					<div
						key="content"
						ref={helpArticlesAnchorRef}
						className={cn("relative", className)}
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
						style={style}
					>
						<EditorContent
							editor={editor}
							className="h-full min-h-[inherit] [&>.prose]:h-full [&>.prose]:min-h-[inherit] [&>.prose]:max-w-full"
						/>
						{editable && (
							<HelpArticlesBridge
								editor={editor}
								anchorRef={helpArticlesAnchorRef}
							/>
						)}
					</div>
					<input
						ref={fileInputRef}
						type="file"
						accept={MEDIA_UPLOAD_ACCEPT}
						className="hidden"
						onChange={handleFileInputChange}
					/>
					<input
						ref={galleryInputRef}
						type="file"
						multiple
						accept="image/jpg,image/jpeg,image/png,image/gif,image/webp"
						className="hidden"
						onChange={handleGalleryInputChange}
					/>
				</AnimatePresence>
			)
		},
	),
)
